import { Chip, Grid, IconButton, OutlinedInput, Typography, Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import { Caption } from "./scribetool";
import { useSelector } from "react-redux";
import { GetPlayingState } from "../../Redux/Reducers/redux-slice-react-player";

const GUTTER_SIZE = 6;
export interface SingleCaptionProps {
  caption: Caption;
  onUpdateCaption?: ((segmentId: string, text: string) => void) | undefined;
  onDeletaCaption?: ((segmentId: string) => void) | undefined;
  setEditSegmentId?: any;
  triggerUpdate?: boolean | undefined;
  isEditing?: boolean | undefined;
  active: boolean;
  scrollTo?: boolean;
  setScrollTo?: (index: number) => void;
  onClickTimecode?: (sec: number) => void;
  currentTime: number | null;
  videoFps: number;
  mode?: "stp" | "sub" | "project";
  index: number;
  showSpeaker?: boolean;
  position: any;
  height?: number;
  top: number;
  left: number;
  right: number;
  setRowHeight?: any;
  search?: string | undefined;
  isCurrentSearchSentence?: boolean;
}
export const SingleCaption: React.FC<SingleCaptionProps> = (props) => {
  const {
    caption,
    onDeletaCaption,
    onUpdateCaption,
    setEditSegmentId,
    triggerUpdate,
    isEditing,
    active,
    scrollTo,
    setScrollTo,
    onClickTimecode,
    currentTime,
    videoFps,
    mode,
    index,
    height,
    left,
    position,
    right,
    top,
    setRowHeight,
    showSpeaker = true,
    search,
    isCurrentSearchSentence,
  } = props;

  const theme = useTheme();

  const currentSec = currentTime;
  const isPlaying = useSelector(GetPlayingState);

  const rowRef = useRef<any>({});
  useEffect(() => {
    if (rowRef.current) {
      setRowHeight &&
        setRowHeight(
          index,
          rowRef.current.clientHeight + GUTTER_SIZE + (index >= 0 && caption.speaker && showSpeaker ? 16 : 0)
        );
    }
  }, [index]);

  useEffect(() => {
    if (active && rowRef.current && isPlaying) {
      rowRef.current.scrollIntoView({
        block: "center",
        inline: "center",
        behavior: "smooth",
      });
      setScrollTo && setScrollTo(-1);
    }
  }, [active, scrollTo, isPlaying]);

  const speakerMemo = useMemo(
    () => (
      <Stack spacing={1} direction={"row"} height={"16px"}>
        {isEditing ? (
          <OutlinedInput
            sx={{ width: "200px", height: "18px !important", margin: "6px !important" }}
            value={"Mr. Dupont"}
          />
        ) : (
          <Typography
            sx={{ marginTop: "0px !important" }}
            color={active ? "text.secondary" : "text.tier"}
            variant="body2"
          >
            {caption.speaker}
          </Typography>
        )}
      </Stack>
    ),
    [isEditing, triggerUpdate, active]
  );

  const chipsMemo = useMemo(() => {
    return (
      <Grid
        display={"flex"}
        flexDirection={"row"}
        justifyContent={mode === "stp" ? "flex-end" : "flex-start"}
        item
        md={mode === "stp" ? 2.5 : 1.5}
      >
        <Chip
          onClick={() => onClickTimecode && onClickTimecode(caption.secIn)}
          label={caption.tcIn}
          sx={{
            width: "92px",
            ml: 0.5,
            mr: 0.5,
            cursor: "pointer",
            backgroundColor: active ? theme.palette.info.main : theme.palette.background.paper,
            color: active ? "text.primary" : "text.secondary",
            borderRadius: "auto",
            fontSize: 12,
          }}
        />
      </Grid>
    );
  }, [caption.tcIn, caption.tcOut, theme.palette.mode, mode, active]);

  const actionsMemo = useMemo(() => {
    return (
      <Grid
        alignContent={"center"}
        alignItems={"center"}
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"flex-end"}
        item
        md={3}
      >
        {isEditing ? (
          <IconButton sx={{ height: 2, width: 2, mr: 1 }} onClick={() => setEditSegmentId && setEditSegmentId(null)}>
            <DoneIcon />
          </IconButton>
        ) : (
          <IconButton
            sx={{ height: 2, width: 2, mr: 1 }}
            onClick={() => setEditSegmentId && setEditSegmentId(caption.segmentId)}
          >
            <EditIcon />
          </IconButton>
        )}
        {isEditing && (
          <IconButton
            sx={{ height: 2, width: 2, mr: 1 }}
            onClick={() => onDeletaCaption && onDeletaCaption(caption.segmentId)}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Grid>
    );
  }, [isEditing, caption.segmentId]);

  const activeWordIndex = useMemo(() => {
    if (!active || !currentSec) return -1;

    return caption.words?.findIndex((x) => currentSec >= x.start / videoFps && currentSec <= x.end / videoFps);
  }, [active, currentSec, caption.words?.length]);

  const foundWordIndexes: number[] = useMemo(() => {
    if (!search) return [];
    const result: number[] = [];
    caption.words?.forEach((x, i) => {
      const text = x.text.trim().toLowerCase();
      if (text.includes(search)) result.push(i);
    });
    return result;
  }, [search]);

  return (
    <Stack
      position={position}
      top={top + GUTTER_SIZE}
      left={left}
      height={height ? height - GUTTER_SIZE : height}
      right={right}
      direction={"column"}
      width={"100%"}
      spacing={1}
      borderRadius={0.5}
      sx={isCurrentSearchSentence ? { background: theme.palette.primary.alphaHover } : undefined}
      p={onClickTimecode ? 0 : 2}
    >
      {caption.speaker && showSpeaker && speakerMemo}
      <Grid mt={"0px !important"} ref={rowRef} width={"100%"} container>
        {onClickTimecode && mode !== "stp" && chipsMemo}
        <Grid
          alignContent={"center"}
          alignItems={"center"}
          display={"flex"}
          flexDirection={"row"}
          flexWrap={"wrap"}
          justifyContent={onClickTimecode ? "start" : "center"}
          item
          md={onClickTimecode ? (mode !== "stp" ? 7.5 : 9.5) : 12}
          px={0.5}
        >
          {isEditing ? (
            <OutlinedInput
              fullWidth
              value={caption.text ?? caption.cue?.text}
              onChange={(e) => onUpdateCaption && onUpdateCaption(caption.segmentId, e.target.value)}
              sx={{ height: "24px !important" }}
            />
          ) : caption.words ? (
            caption.words.map((x, i) => (
              <Typography
                key={i}
                color={
                  foundWordIndexes.length > 0 && foundWordIndexes.includes(i)
                    ? "yellow"
                    : active
                    ? activeWordIndex === i
                      ? theme.palette.info.main
                      : "default"
                    : "text.secondary"
                }
                variant="body2"
                sx={{
                  mr: onClickTimecode ? 0.5 : 1,
                }}
                fontSize={onClickTimecode ? "inital" : "1.5rem"}
                lineHeight={onClickTimecode ? "initial" : "2rem"}
              >
                {x.text}
              </Typography>
            ))
          ) : (
            <Typography
              color={active ? theme.palette.info.main : "default"}
              textAlign={"start"}
              variant="body2"
              sx={{ width: "100%" }}
            >
              {caption.text ?? caption.cue?.text}
            </Typography>
          )}
        </Grid>
        {onClickTimecode && mode === "stp" && chipsMemo}
        {mode !== "stp" && actionsMemo}
      </Grid>
    </Stack>
  );
};
